import DomToImage from "dom-to-image-more";
import { type ComputedRef, type InjectionKey, type Ref, computed, inject, provide } from "vue";
import { SQDCTileTypes } from "@/graphql";

export const ScreenshotContainerInjectionKey = Symbol(
  "ScreenshotContainerInjectionKey",
) as InjectionKey<ComputedRef<HTMLElement | undefined>>;

enum EnableAutomaticScreenshotInjectionKeyPayloadComponent {
  "FileComponent" = "FileComponent",
}

type EnableAutomaticScreenshotInjectionKeyPayload = Record<
  EnableAutomaticScreenshotInjectionKeyPayloadComponent,
  {
    enable: boolean;
    fullScreen?: boolean;
  }
>;

export const EnableAutomaticScreenshotInjectionKey = Symbol(
  "EnableAutomaticScreenshotInjectionKey",
) as InjectionKey<ComputedRef<EnableAutomaticScreenshotInjectionKeyPayload | undefined>>;

let initialScreenshotContainer: ComputedRef<HTMLElement>;
const automaticScreenshotRequests = new WeakSet<object>();
let activeScreenshotCaptures = 0;
let documentOverflowSnapshot:
  | {
      documentElement: { value: string; priority: string };
      body: { value: string; priority: string };
    }
  | undefined;

const screenshotOptions = {
  bgcolor: "white",
  filter: (node: Node) =>
    !(node instanceof Element && node.hasAttribute("data-screenshot-control")),
  // Custom properties are already resolved in computed styles. Copying the complete
  // PrimeVue/theme variable set for every DOM node makes large tiles needlessly expensive.
  filterStyles: (_element: Element, propertyName: string) => !propertyName.startsWith("--"),
  // Reuse default styles for equivalent elements instead of rebuilding every ancestor path.
  styleCaching: "relaxed" as const,
};

function suppressDocumentScrollbars() {
  if (activeScreenshotCaptures === 0) {
    documentOverflowSnapshot = {
      documentElement: {
        value: document.documentElement.style.getPropertyValue("overflow"),
        priority: document.documentElement.style.getPropertyPriority("overflow"),
      },
      body: {
        value: document.body.style.getPropertyValue("overflow"),
        priority: document.body.style.getPropertyPriority("overflow"),
      },
    };
    document.documentElement.style.setProperty("overflow", "hidden", "important");
    document.body.style.setProperty("overflow", "hidden", "important");
  }
  activeScreenshotCaptures += 1;
}

function restoreDocumentScrollbars() {
  activeScreenshotCaptures -= 1;
  if (activeScreenshotCaptures > 0 || documentOverflowSnapshot == null) {
    return;
  }

  const restoreOverflow = (element: HTMLElement, snapshot: { value: string; priority: string }) => {
    if (snapshot.value === "") {
      element.style.removeProperty("overflow");
    } else {
      element.style.setProperty("overflow", snapshot.value, snapshot.priority);
    }
  };

  restoreOverflow(document.documentElement, documentOverflowSnapshot.documentElement);
  restoreOverflow(document.body, documentOverflowSnapshot.body);
  documentOverflowSnapshot = undefined;
}

async function captureWithoutDocumentScrollbars<T>(capture: () => Promise<T>) {
  suppressDocumentScrollbars();
  try {
    return await capture();
  } finally {
    restoreDocumentScrollbars();
  }
}

export function setInitialScreenshotContainer(screenshotContainerProp: ComputedRef<HTMLElement>) {
  initialScreenshotContainer = screenshotContainerProp;
  provide(ScreenshotContainerInjectionKey, screenshotContainerProp);
}

/**
 * TODO: remove need for Vue context
 */
export function useScreenshot(container?: ComputedRef<HTMLElement | undefined | null> | undefined) {
  /**
   * move this inject inside a setup context
   * will remove warnings
   */
  const oldScreenshotContainer = inject(
    ScreenshotContainerInjectionKey,
    computed(() => undefined),
  );
  // An explicitly supplied capture target must never fall back to the app root while its
  // template ref is temporarily unavailable.
  const screenshotContainer = container
    ? computed(() => container.value ?? undefined)
    : oldScreenshotContainer;

  provide(ScreenshotContainerInjectionKey, screenshotContainer);

  async function getImage(fullScreen = false) {
    const screenshotTarget = fullScreen
      ? initialScreenshotContainer.value
      : screenshotContainer.value;
    if (screenshotTarget == null) {
      return;
    }
    const data = await captureWithoutDocumentScrollbars(() =>
      DomToImage.toPng(screenshotTarget, screenshotOptions),
    );
    return data;
  }

  async function getBlob(fullScreen = false) {
    const screenshotTarget = fullScreen
      ? initialScreenshotContainer.value
      : screenshotContainer.value;
    if (screenshotTarget == null) {
      return;
    }
    const data = await captureWithoutDocumentScrollbars(() =>
      DomToImage.toBlob(screenshotTarget, screenshotOptions),
    );
    return data;
  }

  return {
    getImage,
    getBlob,
    downloadImage: async function downloadScreenshot(fileName: string, fullScreen = false) {
      const blob = await getBlob(fullScreen);

      if (blob == null) {
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = fileName;
      link.href = objectUrl;
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    },
  };
}

export function claimAutomaticScreenshotRequest(target: object) {
  if (automaticScreenshotRequests.has(target)) {
    return false;
  }
  automaticScreenshotRequests.add(target);
  return true;
}

export function releaseAutomaticScreenshotRequest(target: object) {
  automaticScreenshotRequests.delete(target);
}

export function waitForRef(ref: Ref<any>) {
  const time = new Date().valueOf();

  return new Promise<void>((resolve, reject) => {
    if (ref.value != null) {
      resolve();
    } else {
      const interval: number = window.setInterval(() => {
        if (ref.value) {
          clearInterval(interval);
          resolve();
        }
        if (new Date().valueOf() - time > 1000 * 5) {
          clearInterval(interval);
          reject();
        }
      }, 100);
    }
  });
}

export function isScreenShotIconEnabled(tileType: ComputedRef<SQDCTileTypes | undefined>) {
  return computed(
    () =>
      tileType.value != null &&
      !(
        [SQDCTileTypes.IFrameTile, SQDCTileTypes.OneDriveTile, SQDCTileTypes.PowerBITile] as (
          | SQDCTileTypes
          | undefined
        )[]
      ).includes(tileType.value),
  );
}
